import { useState, useEffect } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { defectDojoApiRef } from '../api/DefectDojoApi';
import { DefectDojoProduct, Finding, Engagement, deriveStatus } from '../utils/types';

export interface DefectDojoData {
  product: DefectDojoProduct | null;
  findings: Finding[];
  engagements: Engagement[];
  loading: boolean;
  error: Error | null;
}

export function useDefectDojoData(productId: number | null): DefectDojoData {
  const api = useApi(defectDojoApiRef);
  const [product, setProduct] = useState<DefectDojoProduct | null>(null);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [engagements, setEngagements] = useState<Engagement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (productId === null) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      api.getProduct(productId),
      api.getFindings(productId),
      api.getEngagements(productId),
    ])
      .then(([prod, findingsRes, engagementsRes]) => {
        if (cancelled) return;
        setProduct(prod);
        setFindings(
          findingsRes.results.map(f => ({
            ...f,
            status: f.status ?? deriveStatus(f),
          })),
        );
        setEngagements(engagementsRes.results);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, productId]);

  return { product, findings, engagements, loading, error };
}
